/**
 * Global app dialogs — replaces every native browser dialog (window.alert,
 * window.confirm, window.prompt) with a single themed, Apple HIG–styled
 * component rendered by `<AppDialogHost />` (mounted once in `App`).
 *
 * API mirrors the native primitives, but returns Promises:
 *   await appAlert(message)            → void
 *   await appConfirm(message)          → boolean
 *   await appPrompt(message, initial)  → string | null
 * Calls made while another dialog is open are queued and shown in order.
 */

export type AppDialogVariant = "info" | "warning" | "error";

export interface AppDialogRequest {
  id: number;
  /** "info" = single OK button; "confirm"/"error" = Cancel + primary action. */
  kind: "alert" | "confirm" | "prompt";
  variant: AppDialogVariant;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string | null;
  /** Only for kind === "prompt". */
  initialText?: string;
  placeholder?: string;
  /** Resolution gate — captured from the enqueuing Promise by `enqueue`. */
  resolve: (value: string | boolean | null) => void;
}

type Listener = (request: AppDialogRequest | null) => void;

let queue: AppDialogRequest[] = [];
let active: AppDialogRequest | null = null;
let listener: Listener | null = null;
let seq = 0;

function notify() {
  listener?.(active);
}

function enqueue(
  request: Omit<AppDialogRequest, "id" | "resolve">,
): Promise<string | boolean | null> {
  return new Promise((resolve) => {
    // The enqueuing call awaits this promise — keep its resolver on the
    // entry so `settle` can hand the user's answer back to the caller.
    const entry: AppDialogRequest = { ...request, id: ++seq, resolve };
    queue.push(entry);
    // Kick the queue in case no dialog is currently showing.
    if (!active) {
      active = queue.shift() ?? null;
    }
    notify();
  });
}

function settle(value: string | boolean | null) {
  const current = active;
  if (!current) return;
  active = queue.shift() ?? null;
  current.resolve(value);
  notify();
}

/** Subscribe the host component to the currently visible dialog (or null). */
export function subscribeDialogs(fn: Listener): () => void {
  listener = fn;
  // Deliver any dialog already pending before the host mounted.
  if (active) fn(active);
  return () => {
    if (listener === fn) listener = null;
  };
}

/** Escape currently resolves alerts with nothing, confirms with "cancel". */
export function cancelActiveDialog() {
  if (!active) return;
  if (active.kind === "prompt") return settle(null);
  if (active.kind === "confirm") return settle(false);
  settle(undefined as unknown as null);
}

export function confirmActiveDialog(value?: string) {
  if (!active) return;
  if (active.kind === "prompt") return settle(value ?? "");
  if (active.kind === "confirm") return settle(true);
  settle(undefined as unknown as null);
}

export interface AppAlertOptions {
  title?: string;
  confirmLabel?: string;
  variant?: AppDialogVariant;
}

export interface AppConfirmOptions extends AppAlertOptions {
  cancelLabel?: string;
}

/** Drop-in async replacement for `window.alert`. */
export async function appAlert(message: string, options: AppAlertOptions = {}): Promise<void> {
  await enqueue({
    kind: "alert",
    variant: options.variant ?? "info",
    title: options.title ?? "Notice",
    message,
    confirmLabel: options.confirmLabel ?? "OK",
    cancelLabel: null,
  });
}

/** Drop-in async replacement for `window.confirm`. */
export function appConfirm(message: string, options: AppConfirmOptions = {}): Promise<boolean> {
  return enqueue({
    kind: "confirm",
    variant: options.variant ?? "warning",
    title: options.title ?? "Are you sure?",
    message,
    confirmLabel: options.confirmLabel ?? "OK",
    cancelLabel: options.cancelLabel ?? "Cancel",
  }).then((v) => v === true);
}

/** Drop-in async replacement for `window.prompt`. */
export function appPrompt(message: string, initialText = "", options: AppConfirmOptions = {}): Promise<string | null> {
  return enqueue({
    kind: "prompt",
    variant: options.variant ?? "info",
    title: options.title ?? "Input",
    message,
    confirmLabel: options.confirmLabel ?? "OK",
    cancelLabel: options.cancelLabel ?? "Cancel",
    initialText,
    placeholder: "",
  }).then((v) => (typeof v === "string" ? v : null));
}
